import { useEffect } from "react";

type MetaConfig = {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

const SITE_ORIGIN = "https://jiumeijiaju.cn";

function upsertMeta(selector: string, attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function usePageMeta({ title, description, keywords, canonicalPath = "/", jsonLd }: MetaConfig) {
  useEffect(() => {
    document.title = title;
    document.documentElement.lang = "zh-CN";

    upsertMeta('meta[name="description"]', "name", "description", description);
    if (keywords) {
      upsertMeta('meta[name="keywords"]', "name", "keywords", keywords);
    }
    upsertMeta('meta[name="robots"]', "name", "robots", "index,follow");

    const canonicalUrl = `${SITE_ORIGIN}${canonicalPath}`;
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonicalUrl);

    upsertMeta('meta[property="og:type"]', "property", "og:type", "website");
    upsertMeta('meta[property="og:title"]', "property", "og:title", title);
    upsertMeta('meta[property="og:description"]', "property", "og:description", description);
    upsertMeta('meta[property="og:url"]', "property", "og:url", canonicalUrl);
    upsertMeta('meta[property="og:site_name"]', "property", "og:site_name", "蝌蚪爆料");

    upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);

    const scriptId = "page-structured-data";
    const existing = document.getElementById(scriptId);
    if (existing) {
      existing.remove();
    }
    if (jsonLd) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.type = "application/ld+json";
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      const script = document.getElementById(scriptId);
      if (script) {
        script.remove();
      }
    };
  }, [title, description, keywords, canonicalPath, jsonLd]);
}